import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Save } from 'lucide-react';
import { getContactPage, updateContactPage } from '../services/api';
import { toast } from 'sonner';

export const ContactPageManager = () => {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContactData();
  }, []);

  const loadContactData = async () => {
    try {
      const data = await getContactPage();
      setContactData(data);
    } catch (error) {
      toast.error('Failed to load contact page data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateContactPage(contactData);
      toast.success('Contact page updated successfully');
    } catch (error) {
      toast.error('Failed to update contact page');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <CardTitle className="dark:text-white">Contact Page Management</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Hero Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
              Hero Section
            </h3>
            
            <div>
              <Label htmlFor="hero_title">Hero Title</Label>
              <Input
                id="hero_title"
                value={contactData?.hero_title || ''}
                onChange={(e) => setContactData({ ...contactData, hero_title: e.target.value })}
                placeholder="Contact Us"
              />
            </div>

            <div>
              <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
              <Input
                id="hero_subtitle"
                value={contactData?.hero_subtitle || ''}
                onChange={(e) => setContactData({ ...contactData, hero_subtitle: e.target.value })}
                placeholder="Get in touch with our team"
              />
            </div>

            <div>
              <Label htmlFor="page_description">Page Description</Label>
              <Textarea
                id="page_description"
                value={contactData?.page_description || ''}
                onChange={(e) => setContactData({ ...contactData, page_description: e.target.value })}
                rows={3}
                placeholder="Brief description about contacting your company..."
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
              Contact Information
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="office_address">Office Address (Street)</Label>
                <Input
                  id="office_address"
                  value={contactData?.office_address || ''}
                  onChange={(e) => setContactData({ ...contactData, office_address: e.target.value })}
                  placeholder="123 Main Street, Suite 200"
                />
              </div>

              <div>
                <Label htmlFor="office_city_state_zip">City, State, Zip</Label>
                <Input
                  id="office_city_state_zip"
                  value={contactData?.office_city_state_zip || ''}
                  onChange={(e) => setContactData({ ...contactData, office_city_state_zip: e.target.value })}
                  placeholder="Lynchburg, VA 24504"
                />
              </div>

              <div>
                <Label htmlFor="office_phone">Phone Number</Label>
                <Input
                  id="office_phone"
                  value={contactData?.office_phone || ''}
                  onChange={(e) => setContactData({ ...contactData, office_phone: e.target.value })}
                  placeholder="(434) 555-0123"
                />
              </div>

              <div>
                <Label htmlFor="office_email">Email Address</Label>
                <Input
                  id="office_email"
                  type="email"
                  value={contactData?.office_email || ''}
                  onChange={(e) => setContactData({ ...contactData, office_email: e.target.value })}
                  placeholder="info@example.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="office_hours">Office Hours</Label>
              <Textarea
                id="office_hours"
                value={contactData?.office_hours || ''}
                onChange={(e) => setContactData({ ...contactData, office_hours: e.target.value })}
                rows={4}
                placeholder="Monday - Friday: 9:00 AM - 5:00 PM&#10;Saturday: 10:00 AM - 2:00 PM&#10;Sunday: Closed"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Press Enter for new lines
              </p>
            </div>
          </div>

          {/* Map Embed */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
              Map Embed (Optional)
            </h3>

            <div>
              <Label htmlFor="map_embed_url">Google Maps Embed URL</Label>
              <Input
                id="map_embed_url"
                value={contactData?.map_embed_url || ''}
                onChange={(e) => setContactData({ ...contactData, map_embed_url: e.target.value })}
                placeholder="https://www.google.com/maps/embed?..."
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Get embed URL from Google Maps → Share → Embed a map
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-otter-green hover:bg-otter-green-600 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
